import { createAppThunk } from '@mse-reg/app/store';
import { IImportClient, RegistrationImport, RegistrationValidationDto, UasDto } from '@mse-reg/clients';
import { createFileInfo, FileInfo, loadFileAsync } from '@mse-reg/util';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Async Thunks
const validateAsync = createAppThunk(
  'registration-import/VALIDATE',
  async (
    args: { currentStep: number; semesterId: number | null; fileInfo: FileInfo },
    deps: { importClient: IImportClient },
    dispatch,
  ) => {
    const file = await loadFileAsync(args.fileInfo);
    const validationResult = await deps.importClient.validate(args.semesterId, {
      data: file,
      fileName: file?.name,
    });

    if (validationResult.fileIsReadable) {
      if (args.currentStep === 1) {
        dispatch(fileUploadActions.nextStep());
      }

      const partOfUasId = validationResult?.registrationImport?.uasId;
      if (partOfUasId) {
        dispatch(fileUploadActions.selectUas(partOfUasId));
      }
    }

    return validationResult;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const revalidateAsync = createAppThunk(
  'registration-import/VALIDATE_NORMALIZED',
  async (
    args: { currentStep: number; registrationImport: RegistrationImport },
    deps: { importClient: IImportClient },
    dispatch,
  ) => {
    const validationResult = await deps.importClient.revalidate(args.registrationImport);

    if (validationResult) {
      if (args.currentStep === 1) {
        dispatch(fileUploadActions.nextStep());
      }

      const partOfUasId = validationResult?.registrationImport?.uasId;
      if (partOfUasId) {
        dispatch(fileUploadActions.selectUas(partOfUasId));
      }
    }

    return validationResult;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

const submitAsync = createAppThunk(
  'registration-import/SUBMIT',
  async (
    args: { registrationImport: RegistrationImport; fileInfo: FileInfo },
    deps: { importClient: IImportClient },
    dispatch,
  ) => {
    const validationResult = await deps.importClient.submit(args.registrationImport);

    const file = await loadFileAsync(args.fileInfo);
    if (validationResult.registrationImport?.importDbId > 0) {
      await deps.importClient.addFile(validationResult.registrationImport.importDbId, {
        data: file,
        fileName: file?.name,
      });

      dispatch(fileUploadActions.nextStep());
    }

    return validationResult;
  },
  (r) => ({ importClient: r.resolve<IImportClient>('IImportClient') }),
);

type IRegistrationImportState = {
  loading: boolean;
  errorMessage: string | null;
  file: FileInfo | null;
  validationResult: RegistrationValidationDto | null;
  activeStep: number;
  uas: UasDto[];
  selectedUasId: number | '';
};

const initialState: IRegistrationImportState = {
  loading: false,
  errorMessage: null,
  file: null,
  validationResult: null,
  activeStep: 0,
  uas: [],
  selectedUasId: '',
};

export type RegistrationImportState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'fileUpload',
  initialState,
  reducers: {
    goToStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload;
    },
    nextStep(state) {
      state.activeStep = state.activeStep >= 5 ? 5 : state.activeStep + 1;
    },
    lastStep(state) {
      state.activeStep = state.activeStep <= 1 ? 0 : state.activeStep - 1;
    },
    selectFile(state, action: PayloadAction<FileInfo | null>) {
      state.file = action.payload;
    },
    selectUas(state, action: PayloadAction<number | ''>) {
      state.selectedUasId = action.payload;
    },
    updateRegistrationImport(state, action: PayloadAction<RegistrationImport>) {
      if (state.validationResult) {
        state.validationResult = {
          ...state.validationResult,
          registrationImport: { ...action.payload },
        };
      }
    },
    reduceWarningCount(state) {
      if (state.validationResult?.count) {
        state.validationResult = {
          ...state.validationResult,
          count: {
            ...state.validationResult.count,
            warnings: state.validationResult.count.warnings - 1,
            infos: state.validationResult.count.infos + 1,
            userConfirmations: state.validationResult.count.userConfirmations + 1,
          },
          canSubmit: state.validationResult.count.warnings - 1 + state.validationResult.count.errors === 0,
        };
      }
    },
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(validateAsync.pending, (state) => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(validateAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(validateAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.validationResult = { ...action.payload };
      })
      .addCase(revalidateAsync.pending, (state) => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(revalidateAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(revalidateAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.validationResult = action.payload;
      })
      .addCase(submitAsync.pending, (state) => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(submitAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(submitAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.validationResult = action.payload;
      });
  },
});

export const fileUploadReducer = slice.reducer;
export const fileUploadActions = slice.actions;
export const fileUploadThunks = { validateAsync, revalidateAsync, submitAsync };

// Legacy module compatibility
const initActions = () => {
  return Object.freeze({
    nextStep: () => slice.actions.nextStep(),
    lastStep: () => slice.actions.lastStep(),
    reset: () => slice.actions.reset(),
    selectFile: (file: File) => slice.actions.selectFile(createFileInfo(file)),
    resetFileSelection: () => slice.actions.selectFile(null),
    selectUas: (id: number) => slice.actions.selectUas(id),
    goToStep: (step: number) => slice.actions.goToStep(step),
    reduceWarningCount: () => slice.actions.reduceWarningCount(),
    updateRegistrationImport: (registrationImport: RegistrationImport) =>
      slice.actions.updateRegistrationImport(registrationImport),
    validateAsync: (currentStep: number, semesterId: number | null, fileInfo: FileInfo) =>
      validateAsync({ currentStep, semesterId, fileInfo }),
    revalidateAsync: (currentStep: number, registrationImport: RegistrationImport) =>
      revalidateAsync({ currentStep, registrationImport }),
    submitAsync: (registrationImport: RegistrationImport, fileInfo: FileInfo) =>
      submitAsync({ registrationImport, fileInfo }),
  });
};

export type RegistrationImportsActions = ReturnType<typeof initActions>;

export const fileUploadModule = {
  initActions: () => initActions(),
  reducer: slice.reducer,
};
